<script>
import MainMenuStudentView from '../../shared/views/MainMenuStudentView';
import ModuleStudentAccountComponent from '../features/ModuleStudentAccountComponent';
import UserInitialsHelper from '../../../core/shared/helpers/UserInitialsHelper';
import ModalStudentView from '../../students/views/modals/ModalStudentView';
import ModalStudentAccountSubscriptionView from './ModalStudentAccountSubscriptionView';
import ModalStudentInfoView from './ModalStudentInfoView';

export default {
  components: {
    'main-menu-student-view': MainMenuStudentView,
    'modal-student-view': ModalStudentView,
    'modal-student-account-subscription-view': ModalStudentAccountSubscriptionView,
    'modal-student-info-view': ModalStudentInfoView,
  },
  extends: ModuleStudentAccountComponent,
  data() {
    return {
      activeTab: 'about',
    };
  },
  computed: {
    fullName() {
      if (!this.session || !this.session.user) return '';
      return `${this.session.user.firstName || ''} ${this.session.user.lastName || ''}`.trim();
    },
    roleName() {
      return this.session && this.session.role ? this.session.role.name : 'Estudiante';
    },
    // Suscripción con la fecha de término más reciente
    latestSubscription() {
      if (!this.subscriptions || this.subscriptions.length === 0) return null;
      return [...this.subscriptions].sort(
        (a, b) => new Date(b.subscriptionEndDate) - new Date(a.subscriptionEndDate),
      )[0];
    },
    isSubscriptionActive() {
      return !!this.latestSubscription
        && new Date(this.latestSubscription.subscriptionEndDate) >= new Date();
    },
    hasSocialLinks() {
      return this.session && this.session.student
        && (this.session.student.pageUrlFacebook || this.session.student.pageUrlInstagram);
    },
    /**
     * Iniciales del usuario de la sesión.
     * Se muestran cuando el usuario no tiene imagen de perfil.
     */
    userInitials() {
      const user = (this.session && this.session.user) ? this.session.user : null;
      return UserInitialsHelper.getInitials(user);
    },
    /**
     * Indica si el avatar debe mostrar la imagen del usuario.
     */
    hasProfileImage() {
      return !!(this.profileImageUrl) && !this.avatarLoadError;
    },
  },
  methods: {
    onAvatarError() {
      // La imagen falló al cargar: se muestra el avatar con iniciales.
      this.avatarLoadError = true;
    },
  },
  watch: {
    profileImageUrl() {
      // Si cambia la imagen (p. ej. al actualizar el perfil), se reintenta la carga.
      this.avatarLoadError = false;
    },
  },
};
</script>

<template>
  <div>
    <main-menu-student-view />
    <div class="wrapper">
      <div class="sa4d25">
        <div class="container-fluid">
          <div class="row">
            <div class="col-12">
              <div class="section3125">
                <!-- Encabezado de página -->
                <div class="stream_page_header">
                  <div class="stream_header_left">
                    <div class="stream_header_icon">
                      <i class="fas fa-user-circle" />
                    </div>
                    <div class="stream_header_text">
                      <h4 class="item_title">
                        Mi Cuenta
                      </h4>
                      <p class="stream_header_subtitle">
                        Administra tu perfil, revisa tu actividad y el estado de tu suscripción
                      </p>
                    </div>
                  </div>
                  <div class="stream_header_right">
                    <span
                      v-if="latestSubscription"
                      class="account_status_pill"
                      :class="isSubscriptionActive ? 'is_active' : 'is_expired'"
                    >
                      <span class="pill_dot" />
                      {{ isSubscriptionActive ? 'Suscripción activa' : 'Suscripción vencida' }}
                    </span>
                  </div>
                </div>

                <!-- Tarjeta de perfil -->
                <div
                  v-if="session"
                  class="account_profile_card"
                >
                  <div class="account_profile_cover" />
                  <div class="account_profile_body">
                    <div class="account_profile_main">
                      <img
                        v-if="hasProfileImage"
                        :src="profileImageUrl"
                        :alt="fullName"
                        class="account_avatar"
                        @error="onAvatarError"
                      >
                      <div
                        v-else
                        class="account_avatar account_avatar_initials"
                        role="img"
                        :aria-label="fullName"
                      >
                        {{ userInitials }}
                      </div>
                      <div class="account_identity">
                        <h2 class="account_name">
                          {{ fullName }}
                        </h2>
                        <span class="account_email">
                          <i class="far fa-envelope" /> {{ session.user.email }}
                        </span>
                        <div class="account_badges">
                          <span class="account_role_badge">
                            <i class="fas fa-user-graduate" /> {{ roleName }}
                          </span>
                          <template v-if="hasSocialLinks">
                            <a
                              v-if="session.student.pageUrlFacebook"
                              :href="session.student.pageUrlFacebook"
                              target="_blank"
                              rel="noopener"
                              class="account_social_btn is_fb"
                              title="Facebook"
                            ><i class="fab fa-facebook-f" /></a>
                            <a
                              v-if="session.student.pageUrlInstagram"
                              :href="session.student.pageUrlInstagram"
                              target="_blank"
                              rel="noopener"
                              class="account_social_btn is_ig"
                              title="Instagram"
                            ><i class="fab fa-instagram" /></a>
                          </template>
                        </div>
                      </div>
                    </div>
                    <div class="account_profile_actions">
                      <button
                        type="button"
                        class="account_btn_primary"
                        @click="onEditProfile"
                      >
                        <i class="fas fa-pen" /> Editar perfil
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Estadísticas -->
                <div
                  v-if="session && session.student"
                  class="account_stats_grid"
                >
                  <div class="account_stat_card">
                    <div class="account_stat_icon is_blue">
                      <i class="fas fa-star" />
                    </div>
                    <div class="account_stat_body">
                      <span class="account_stat_value">{{ session.student.totalFavorites }}</span>
                      <span class="account_stat_label">Favoritos</span>
                    </div>
                  </div>
                  <div class="account_stat_card">
                    <div class="account_stat_icon is_green">
                      <i class="fas fa-thumbs-up" />
                    </div>
                    <div class="account_stat_body">
                      <span class="account_stat_value">{{ session.student.totalLiked }}</span>
                      <span class="account_stat_label">Te gustan</span>
                    </div>
                  </div>
                  <div class="account_stat_card">
                    <div class="account_stat_icon is_red">
                      <i class="fas fa-thumbs-down" />
                    </div>
                    <div class="account_stat_body">
                      <span class="account_stat_value">{{ session.student.totalDisliked }}</span>
                      <span class="account_stat_label">No te gustan</span>
                    </div>
                  </div>
                  <div class="account_stat_card">
                    <div class="account_stat_icon is_navy">
                      <i class="fas fa-book-open" />
                    </div>
                    <div class="account_stat_body">
                      <span class="account_stat_value">{{ session.student.totalEnrolled }}</span>
                      <span class="account_stat_label">En curso</span>
                    </div>
                  </div>
                </div>

                <!-- Tabs: Acerca de mí / Suscripción -->
                <div class="la5lo1">
                  <el-tabs
                    v-model="activeTab"
                    class="stream_tabs"
                  >
                    <el-tab-pane name="about">
                      <template #label>
                        <span class="stream_tab_label">
                          <i class="fas fa-id-card" /> Acerca de mí
                        </span>
                      </template>
                      <div class="account_panel">
                        <div
                          v-if="sessionAbout"
                          class="account_about_text"
                        >
                          {{ sessionAbout }}
                        </div>
                        <div
                          v-else
                          class="stream_empty_state"
                        >
                          <div class="stream_empty_icon">
                            <i class="fas fa-feather-alt" />
                          </div>
                          <h4>Aún no has escrito sobre ti</h4>
                          <p>Cuéntale a tus profesores quién eres y cuáles son tus metas académicas.</p>
                          <button
                            type="button"
                            class="account_btn_primary mt-3"
                            @click="onEditProfile"
                          >
                            <i class="fas fa-pen" /> Completar perfil
                          </button>
                        </div>
                      </div>
                    </el-tab-pane>

                    <el-tab-pane name="subscription">
                      <template #label>
                        <span class="stream_tab_label">
                          <i class="fas fa-credit-card" /> Suscripción
                        </span>
                      </template>

                      <!-- Resumen de la suscripción vigente -->
                      <div class="stream_toolbar">
                        <div class="account_sub_summary">
                          <template v-if="latestSubscription">
                            <span class="account_sub_plan">
                              <i class="fas fa-crown" /> {{ latestSubscription.subscriptionPlan }}
                            </span>
                            <span class="account_sub_meta">
                              {{ isSubscriptionActive ? 'Vigente hasta' : 'Venció el' }}
                              <strong>{{ formatDate(latestSubscription.subscriptionEndDate) }}</strong>
                            </span>
                          </template>
                          <span
                            v-else
                            class="account_sub_meta"
                          >
                            No cuentas con una suscripción registrada.
                          </span>
                        </div>
                        <button
                          type="button"
                          class="account_btn_primary"
                          @click="onUpdateSubscription"
                        >
                          <i class="fas fa-sync-alt" /> Actualizar inscripción
                        </button>
                      </div>

                      <div class="account_panel is_flush">
                        <el-table
                          :data="subscriptions"
                          empty-text="Aún no tienes pagos de suscripción registrados."
                          style="width: 100%"
                        >
                          <el-table-column
                            label="PLAN"
                            min-width="230"
                          >
                            <template #default="scope">
                              <span class="account_table_plan">{{ scope.row.subscriptionPlan }}</span>
                            </template>
                          </el-table-column>
                          <el-table-column
                            label="PAGO"
                            align="center"
                            width="140"
                          >
                            <template #default="scope">
                              {{ formatMoney(scope.row.amount) }}
                            </template>
                          </el-table-column>
                          <el-table-column
                            label="FECHA DE PAGO"
                            align="center"
                            width="150"
                          >
                            <template #default="scope">
                              {{ formatDate(scope.row.subscriptionDate) }}
                            </template>
                          </el-table-column>
                          <el-table-column
                            label="FIN SUSCRIPCIÓN"
                            align="center"
                            width="160"
                          >
                            <template #default="scope">
                              {{ formatDate(scope.row.subscriptionEndDate) }}
                            </template>
                          </el-table-column>
                        </el-table>
                      </div>
                    </el-tab-pane>
                  </el-tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal-student-view
      v-if="modalItem.isVisible"
      :form-data="item"
      :conf="modalItem"
      :show-status="false"
    />
    <modal-student-account-subscription-view
      v-if="modalSubscriptionItem.isVisible"
      :form-data="subscriptionItem"
      :conf="modalSubscriptionItem"
    />
    <modal-student-info-view
      v-if="modalStudentInfoItem.isVisible"
      :form-data="studentInfoItem"
      :conf="modalStudentInfoItem"
    />
    <div v-loading.fullscreen.lock="fullLoader" />
  </div>
</template>
